import { useEffect, useState } from "react";

import type { EarlyVote, EarlyVoteCounts } from "./types";
import { splitCsvLine } from "./useCounty2024";

/* 2026 ballots already cast, by state (daily) and county (latest), from Call
   the Map: free to use with credit, open to cross-origin requests. States it
   doesn't cover return the header row only. */
const earlyVoteUrl = (abbr: string) =>
  `https://www.callthemap.com/early-vote/data/${abbr}.csv`;

const count = (cell: string | undefined): number | null =>
  cell ? Number(cell) : null;

const counts = (row: Record<string, string>): EarlyVoteCounts => ({
  dem: count(row.dem),
  inPerson: count(row.in_person),
  mail: count(row.mail),
  other: count(row.unaffiliated_other),
  rep: count(row.rep),
  total: Number(row.total),
});

/** Latest statewide count and county counts; null when none is reported. */
export const parseEarlyVoteCsv = (text: string): EarlyVote | null => {
  const [header, ...lines] = text.trim().split(/\r?\n/);
  const columns = splitCsvLine(header).map((c) => c.trim());
  const rows = lines.map((line) => {
    const cells = splitCsvLine(line);
    return Object.fromEntries(
      columns.map((column, i) => [column, cells[i]?.trim() ?? ""])
    );
  });

  const latest = rows
    .filter((row) => row.row_type === "daily" && row.total)
    .sort((a, b) => b.date.localeCompare(a.date))[0];
  if (!latest) return null;
  return {
    counties: rows
      .filter((row) => row.row_type === "county" && row.total)
      .map((row) => ({ ...counts(row), name: row.county }))
      .sort((a, b) => b.total - a.total),
    credit: latest.credit || "Call the Map (callthemap.com/early-vote)",
    date: latest.date,
    daysOut: count(latest.days_out),
    state: counts(latest),
  };
};

const cache = new Map<string, Promise<EarlyVote | null>>();

const load = async (abbr: string): Promise<EarlyVote | null> => {
  const response = await fetch(earlyVoteUrl(abbr));
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return parseEarlyVoteCsv(await response.text());
};

interface Result {
  abbr: string;
  earlyVote: EarlyVote | null;
  failed: boolean;
}

const useEarlyVote = (abbr: string) => {
  const [result, setResult] = useState<Result | null>(null);

  useEffect(() => {
    let cancelled = false;
    let request = cache.get(abbr);
    if (!request) {
      request = load(abbr);
      cache.set(abbr, request);
    }
    request
      .then((earlyVote) => {
        if (!cancelled) setResult({ abbr, earlyVote, failed: false });
      })
      .catch(() => {
        cache.delete(abbr);
        if (!cancelled) setResult({ abbr, earlyVote: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [abbr]);

  const current = result?.abbr === abbr ? result : null;
  const status: "error" | "loading" | "ready" =
    current === null ? "loading" : current.failed ? "error" : "ready";
  return { earlyVote: current?.earlyVote ?? null, status };
};

export default useEarlyVote;
